import { useMemo } from 'react';
import { Cog, CheckCircle, XCircle, Loader2, RefreshCw, AlertTriangle, Play } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/data-display/StatCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { Can } from '@/components/auth/Can';
import { ROUTES, UNE_PERM } from '@/constants';
import { useJobs } from '@/features/jobs/hooks';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Operaciones', path: ROUTES.JOBS_CALENDAR },
  { label: 'Jobs', path: ROUTES.JOBS_CALENDAR },
];

const STATUS_CFG: Record<string, { variant: 'default' | 'success' | 'warning' | 'danger' | 'info'; label: string }> = {
  COMPLETED: { variant: 'success', label: 'Completado' },
  RUNNING: { variant: 'info', label: 'Ejecutando' },
  FAILED: { variant: 'danger', label: 'Fallido' },
  PENDING: { variant: 'default', label: 'Pendiente' },
};

export function JobsPage() {
  const { jobs, isLoading, isError, refetch, runJob, isRunning } = useJobs();
  const items = jobs ?? [];

  const stats = useMemo(() => ({
    total: items.length,
    completed: items.filter((j) => j.lastStatus === 'COMPLETED').length,
    failed: items.filter((j) => j.lastStatus === 'FAILED').length,
    running: items.filter((j) => j.lastStatus === 'RUNNING').length,
  }), [items]);

  if (isLoading) return (<div><PageHeader title="Monitor de Jobs" breadcrumbs={BREADCRUMBS} /><div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={`s-${i.toString()}`} variant="rectangular" height={80} />)}</div></div>);
  if (isError) return (<div><PageHeader title="Monitor de Jobs" breadcrumbs={BREADCRUMBS} /><div className="flex flex-col items-center rounded-lg border border-border bg-card px-6 py-16"><AlertTriangle size={48} className="mb-4 text-muted-foreground" /><h3 className="text-lg font-semibold">Error al cargar jobs</h3><Button className="mt-4" onClick={() => refetch()}><RefreshCw size={16} /> Reintentar</Button></div></div>);

  return (
    <div>
      <PageHeader title="Monitor de Jobs" subtitle="Estado y ejecucion de jobs del sistema" breadcrumbs={BREADCRUMBS} actions={<div className="flex items-center gap-2"><HelpButton pageKey="jobs" /><Button onClick={() => refetch()}><RefreshCw size={16} /> Actualizar</Button></div>} />
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Total Jobs" value={stats.total} icon={Cog} />
          <StatCard title="Completados" value={stats.completed} icon={CheckCircle} />
          <StatCard title="Fallidos" value={stats.failed} icon={XCircle} />
          <StatCard title="En Ejecucion" value={stats.running} icon={Loader2} />
        </div>

        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-6 py-4"><h3 className="text-lg font-semibold">Jobs del Sistema</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-6 py-3 font-medium">Nombre</th><th className="px-6 py-3 font-medium">Proceso</th><th className="px-6 py-3 font-medium">Frecuencia</th><th className="px-6 py-3 font-medium">Hora</th><th className="px-6 py-3 font-medium">Descripcion</th><th className="px-6 py-3 font-medium">Ultima Ejecucion</th><th className="px-6 py-3 font-medium">Estado</th><th className="px-6 py-3 font-medium">Accion</th>
              </tr></thead>
              <tbody>
                {items.map((job) => {
                  const cfg = STATUS_CFG[job.lastStatus] ?? { variant: 'default' as const, label: 'Pendiente' };
                  return (
                    <tr key={job.name} className="border-b border-border/50">
                      <td className="px-6 py-3 font-medium text-foreground">{job.name}</td>
                      <td className="px-6 py-3 text-muted-foreground">{job.process}</td>
                      <td className="px-6 py-3"><Badge variant="default">{job.frequency}</Badge></td>
                      <td className="px-6 py-3 tabular-nums text-muted-foreground">{job.scheduledTime}</td>
                      <td className="px-6 py-3 text-muted-foreground max-w-[200px] truncate">{job.description}</td>
                      <td className="px-6 py-3 text-xs text-muted-foreground">{job.lastExecution ? new Date(job.lastExecution).toLocaleString('es-MX') : '-'}</td>
                      <td className="px-6 py-3"><Badge variant={cfg.variant}>{cfg.label}</Badge></td>
                      <td className="px-6 py-3">
                        <Can permission={UNE_PERM.JOB_RUN}>
                          <Button size="sm" variant="outline" disabled={job.lastStatus === 'RUNNING' || isRunning} onClick={() => runJob(job.name)}>
                            <Play size={12} /> Ejecutar
                          </Button>
                        </Can>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
